/**
 * Request sharing for the imperative `client().query(...)` hooks.
 *
 * `client()` builds a fresh ApolloClient per call, so neither Apollo's cache
 * nor its in-flight deduplication spans two hooks: every mounted consumer of a
 * query sends its own request (PropertyIntelligenceList went out 6x on
 * /campaigns). `sharedQuery` gives those loaders one promise per key:
 *
 * - identical requests in flight at the same time share one network call;
 * - a settled result is reused for `maxAgeMs` (0 = in-flight sharing only);
 * - failures are never reused;
 * - any GraphQL mutation (see `invalidateOnMutationLink` in apollo.ts) or an
 *   explicit `invalidateSharedQueries()` drops everything, so a list read
 *   before a create/archive is never served after it;
 * - `after: sharedQuerySeq()` taken when a user asks for a refetch skips every
 *   result that started before that moment, while concurrent refetches still
 *   share one request.
 *
 * Keys are scoped to the active session token: a result read with one
 * organization's token is never handed to another.
 */
// The same read as `getActiveSessionToken`, without importing the Apollo
// client through `@services/sessionToken`.
import { StorageKey, getItem } from '@services/storageService';

const sessionToken = (): string => {
  try {
    return (getItem(StorageKey.TOKEN) as string | null) || '';
  } catch {
    return '';
  }
};

type Entry = {
  promise: Promise<unknown>;
  seq: number;
  epoch: number;
  settledAt: number | null;
  value: unknown;
};

const entries = new Map<string, Entry>();
let seq = 0;
let epoch = 0;

/** Sequence number of the latest request; pass as `after` to force a refetch. */
export const sharedQuerySeq = (): number => seq;

/** Forgets every shared result (and stops new callers joining in-flight ones). */
export const invalidateSharedQueries = (): void => {
  epoch += 1;
  entries.clear();
};

export const sharedQueryKey = (operation: string, variables?: unknown) =>
  `${sessionToken()}|${operation}|${JSON.stringify(variables ?? null)}`;

type Freshness = { maxAgeMs?: number; after?: number };

const usable = (
  entry: Entry | undefined,
  { maxAgeMs = 0, after = -1 }: Freshness,
): entry is Entry =>
  !!entry &&
  entry.epoch === epoch &&
  entry.seq > after &&
  (entry.settledAt === null ||
    (maxAgeMs > 0 && Date.now() - entry.settledAt <= maxAgeMs));

/** The shared promise for `key` if one is usable, without starting a load. */
export function peekSharedQuery<T>(
  key: string,
  freshness: Freshness = {},
): Promise<T> | undefined {
  const entry = entries.get(key);
  return usable(entry, freshness) ? (entry.promise as Promise<T>) : undefined;
}

/** The settled value for `key` if usable, so a remount can render it at once. */
export function peekSharedValue<T>(
  key: string,
  freshness: Freshness = {},
): { value: T } | undefined {
  const entry = entries.get(key);
  return usable(entry, freshness) && entry.settledAt !== null
    ? { value: entry.value as T }
    : undefined;
}

/**
 * Registers `promise` as the shared request for `key` (replacing any). The
 * settled result is kept `retainMs` (0 = dropped as soon as it lands), so
 * snapshots of pages left behind do not pile up in memory.
 */
export function storeSharedQuery<T>(
  key: string,
  promise: Promise<T>,
  retainMs = 0,
): Promise<T> {
  seq += 1;
  const entry: Entry = {
    promise: Promise.resolve(),
    seq,
    epoch,
    settledAt: null,
    value: undefined,
  };
  entry.promise = promise.then(
    (value) => {
      entry.value = value;
      entry.settledAt = Date.now();
      const evict = () => {
        if (entries.get(key) === entry) entries.delete(key);
      };
      if (retainMs > 0) setTimeout(evict, retainMs);
      else evict();
      return value;
    },
    (error: unknown) => {
      if (entries.get(key) === entry) entries.delete(key);
      throw error;
    },
  );
  // Callers get the rejection; an entry nobody awaited must not surface as an
  // unhandled rejection.
  entry.promise.catch(() => undefined);
  entries.set(key, entry);
  return entry.promise as Promise<T>;
}

export function sharedQuery<T>(
  key: string,
  load: () => Promise<T>,
  freshness: Freshness = {},
): Promise<T> {
  return (
    peekSharedQuery<T>(key, freshness) ||
    storeSharedQuery(key, load(), freshness.maxAgeMs)
  );
}

/** Test helper: forget all state between tests. */
export const resetSharedQueries = (): void => {
  entries.clear();
  seq = 0;
  epoch = 0;
};
